import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import type { Permission } from '../../types/permissions';
import type { AccessArea } from '../../access/areas';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredPermission?: Permission | string;
  /** Any of these access-matrix areas, at view or above. */
  requiredArea?: AccessArea | AccessArea[];
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredArea,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, token, hasPermission, canArea } = useAuthStore();

  // Check if user is authenticated
  if (!isAuthenticated || !token) {
    return <Navigate to={fallbackPath} replace />;
  }

  // If a specific permission or area is required, check it
  if (requiredPermission || requiredArea) {
    const areas = requiredArea ? ([] as AccessArea[]).concat(requiredArea) : [];
    const allowed = (!requiredPermission || hasPermission(requiredPermission))
      && (!areas.length || areas.some((a) => canArea(a)));
    
    if (!allowed) {
      // User is authenticated but doesn't have permission
      return (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          maxWidth: '600px',
          margin: '4rem auto'
        }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
            {requiredPermission ? <>Required permission: <code>{requiredPermission}</code></> : 'Ask your company owner for access.'}
          </p>
          <button 
            onClick={() => window.history.back()}
            style={{
              marginTop: '1.5rem',
              padding: '0.5rem 1.5rem',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Go Back
          </button>
        </div>
      );
    }
  }

  // If children are provided, render them (for wrapping specific routes)
  // Otherwise, render Outlet (for nested routes)
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
